// src/screens/LoginScreen.tsx
import React, { useState, useRef } from 'react';
import './parts/LoginScreen.styles.css';
import { Checkbox } from 'primereact/checkbox';
import { Message } from 'primereact/message';
import { Toast } from 'primereact/toast';
import { useAuth } from '../../hooks/useAuth';
import { GlobalButton } from '../../components/GlobalComponents/GlobalButton';
import { GlobalInputField } from '../../components/GlobalComponents/GlobalInputField';

const LoginScreen: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const { login } = useAuth();
  const toast = useRef<Toast>(null);

  const validateForm = () => {
    let isValid = true;

    if (!email.trim()) {
      toast.current?.show({ severity: 'warn', summary: 'Validation Error', detail: 'Please enter your username or email', life: 3000 });
      isValid = false;
    }

    if (!password.trim()) {
      toast.current?.show({ severity: 'warn', summary: 'Validation Error', detail: 'Please enter your password', life: 3000 });
      isValid = false;
    }

    return isValid;
  };

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError('');

    if (!validateForm()) return;

    setSubmitting(true);
    try {
      await login(email.trim(), password);
    } catch (err: any) {
      const errorMsg = err.message || 'Login failed. Please check your credentials.';
      setLoginError(errorMsg);
      toast.current?.show({ severity: 'error', summary: 'Login Failed', detail: errorMsg, life: 4000 });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Toast ref={toast} position="top-right" />

      <div className="login-container">
        <div className="login-card">
          {/* Left Panel */}
          <div className="login-left animated-gradient">
            <div className="swirl-layer-1"></div>
            <div className="swirl-layer-2"></div>

            <div className="login-left-content">
              <div>
                <svg className="brand-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2L12 12M12 12L12 22M12 12L22 12M12 12L2 12" stroke="white" strokeWidth="2.5" strokeLinecap="round" />
                </svg>
              </div>

              <div>
                <p className="tagline">You can easily</p>
                <h1 className="main-heading">
                  Get access your personal hub for clarity and productivity
                </h1>
              </div>
            </div>
          </div>

          {/* Right Panel */}
          <div className="login-right">
            <div>
              <h2 className="form-title">Welcome to SkoolPillar</h2>
              <p className="form-subtitle">Sign in to your account</p>
            </div>

            {loginError && (
              <Message severity="error" className='custominline' text={loginError} style={{ marginBottom: '1.5rem', width: '100%' }} />
            )}

            <div className="form-content">
              <form onSubmit={handleLoginSubmit}>
                <div className="input-wrapper">
                  <GlobalInputField
                    label="Username or Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your username or email"
                    type="text"
                    required
                    icon="pi pi-user"
                    autoComplete="username"
                  />
                </div>

                <div className="input-wrapper">
                  <GlobalInputField
                    label="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    type="password"
                    required
                    icon="pi pi-lock"
                    autoComplete="current-password"
                  />
                </div>

                <div className="remember-forgot">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Checkbox inputId="remember" checked={rememberMe} onChange={(e) => setRememberMe(e.checked ?? false)} />
                    <label htmlFor="remember" style={{ color: '#4b5563', cursor: 'pointer' }}>Remember me</label>
                  </div>
                  <a href="#" className="forgot-link">Forgot Password?</a>
                </div>

                <GlobalButton
                  label={submitting ? 'Signing in...' : 'Sign In'}
                  type="submit"
                  variant="primary"
                  size="large"
                  fullWidth
                  loading={submitting}
                  disabled={submitting}
                  icon="pi pi-sign-in"
                  iconPos="left"
                />

                <p className="signup-text">
                  Don't have an account? <a href="#" className="signup-link">Contact Administrator</a>
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LoginScreen;